import { useEffect, useMemo, useRef, useState } from 'react'
import { parseItemsCsv } from '../lib/csvParser.js'
import { formatMoney } from '../lib/format.js'

// Modal for bulk-importing items from a POS CSV export.
//
// Targets Toast's *Item Sales* and *Menu Items* reports (and any
// other POS export with item-level rows). The parser auto-detects
// columns so the same UI handles different vendors.
//
// Match strategy: case-insensitive name match against existing items.
// Existing items are kept (no overwrite) — only their empty fields
// get filled in. New items are inserted with sale_price set, cost
// left blank by default (manager fills in later) unless the manager
// opts into the 30%-of-price cost estimate.
export default function ItemsImportModal({
  open,
  onClose,
  onImport,        // (rows, options) → Promise<{ inserted, updated, skipped }>
  existingItems,   // current items list — for matching preview
}) {
  const fileRef = useRef(null)
  const [filename, setFilename]   = useState('')
  const [parsed, setParsed]       = useState(null)
  const [estimateCost, setEstimateCost] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError]         = useState(null)

  useEffect(() => {
    if (open) {
      setFilename('')
      setParsed(null)
      setEstimateCost(false)
      setError(null)
      setSubmitting(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }, [open])

  // Match preview: which rows are new vs existing in the catalog.
  // Done on every parse so the manager sees the impact before importing.
  const matchPreview = useMemo(() => {
    if (!parsed?.rows?.length) return null
    const existingByName = new Map()
    for (const i of existingItems ?? []) {
      existingByName.set((i.name || '').toLowerCase().trim(), i)
    }
    let toInsert = 0
    let toUpdate = 0
    let unchanged = 0
    const topSellers = []
    for (const row of parsed.rows) {
      const key = row.name.toLowerCase().trim()
      const existing = existingByName.get(key)
      if (existing) {
        // We'd update only if the existing row is missing fields the CSV provides
        const wouldUpdate =
          (existing.category == null && row.category) ||
          (existing.sale_price == null && row.sale_price != null)
        if (wouldUpdate) toUpdate++
        else unchanged++
      } else {
        toInsert++
      }
      if (row.quantity_sold) topSellers.push(row)
    }
    topSellers.sort((a, b) => (b.quantity_sold || 0) - (a.quantity_sold || 0))
    return {
      toInsert, toUpdate, unchanged,
      topSellers: topSellers.slice(0, 5),
      hasVelocity: topSellers.length > 0,
    }
  }, [parsed, existingItems])

  if (!open) return null

  async function handleFile(e) {
    const file = e.target.files?.[0]
    if (!file) return
    setFilename(file.name)
    setError(null)
    try {
      const text = await file.text()
      const result = parseItemsCsv(text)
      setParsed(result)
      if (result.rows.length === 0) {
        setError(result.errors[0] ?? 'No usable item rows found in this CSV.')
      }
    } catch (e2) {
      // eslint-disable-next-line no-console
      console.error('[ItemsImportModal] parse failed', e2)
      setError('Could not read this file. Make sure it\u2019s a CSV export.')
    }
  }

  async function handleConfirm() {
    if (submitting || !parsed?.rows?.length) return
    setSubmitting(true)
    setError(null)
    try {
      await onImport(parsed.rows, { estimateCost })
      onClose?.()
    } catch (e2) {
      // eslint-disable-next-line no-console
      console.error('[ItemsImportModal] import failed', e2)
      setError('Could not save to the database. Try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-40">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/50 animate-fade-in"
      />

      <div
        className="animate-sheet-up absolute inset-x-0 bottom-0 max-h-[92vh] overflow-y-auto rounded-t-2xl bg-white shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-label="Import items from POS"
      >
        <div className="sticky top-0 z-10 bg-white pt-2">
          <div className="mx-auto h-1.5 w-12 rounded-full bg-black/15" />
          <div className="flex items-center justify-between px-4 py-3">
            <h2 className="text-xl font-medium">Import items from POS</h2>
            <button
              type="button"
              onClick={onClose}
              className="min-h-btn min-w-[48px] rounded-lg px-3 text-base font-medium text-black/70 hover:bg-black/5"
              aria-label="Cancel"
            >
              Cancel
            </button>
          </div>
          {error && (
            <div role="alert" className="mx-4 mb-2 rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white shadow">
              {error}
            </div>
          )}
        </div>

        <div className="space-y-4 px-4 pb-4">
          {/* Instructions */}
          <div className="rounded-xl border border-blue-200 bg-blue-50/40 p-3 text-sm leading-relaxed text-blue-900/90">
            <div className="mb-1 font-medium">From Toast Web — two options:</div>
            <ul className="list-disc space-y-1 pl-5 text-[13px]">
              <li>
                <span className="font-medium">Reports → Item Sales</span> — best
                if you want to see what's actually selling (fills in price + sales velocity).
              </li>
              <li>
                <span className="font-medium">Menus → Export menu</span> —
                best for first-time setup (full catalog including items that
                haven't sold recently).
              </li>
            </ul>
            <div className="mt-2 text-xs text-blue-900/70">
              Square, Clover, and Lightspeed item exports also work — the
              parser auto-detects column names.
            </div>
          </div>

          {/* File picker */}
          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="items-file">
              Choose CSV file
            </label>
            <input
              ref={fileRef}
              id="items-file"
              type="file"
              accept=".csv,text/csv"
              onChange={handleFile}
              className="block w-full rounded-lg border border-black/15 px-3 py-3 text-sm file:mr-3 file:rounded-md file:border-0 file:bg-blue-600 file:px-3 file:py-2 file:text-sm file:font-medium file:text-white hover:file:bg-blue-700"
            />
            {filename && (
              <p className="mt-1 truncate text-xs text-black/55">
                Selected: <span className="font-medium text-black/80">{filename}</span>
              </p>
            )}
          </div>

          {/* Detected columns */}
          {parsed?.headers?.length > 0 && (
            <div className="rounded-xl border border-black/10 bg-black/[0.02] p-3 text-xs">
              <div className="mb-1 font-medium text-black/60">
                Detected columns
              </div>
              <div className="space-y-0.5">
                <DetectedRow label="Item name"  idx={parsed.columns?.nameIdx}     headers={parsed.headers} />
                <DetectedRow label="Category"   idx={parsed.columns?.categoryIdx} headers={parsed.headers} optional />
                <DetectedRow label="Sale price" idx={parsed.columns?.priceIdx}    headers={parsed.headers} optional />
                <DetectedRow label="Qty sold"   idx={parsed.columns?.qtyIdx}      headers={parsed.headers} optional />
                <DetectedRow label="Toast GUID" idx={parsed.columns?.guidIdx}     headers={parsed.headers} optional />
              </div>
            </div>
          )}

          {/* Match preview */}
          {matchPreview && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4">
              <div className="mb-1 text-xs font-medium text-emerald-900/70">
                Preview
              </div>
              <div className="text-sm text-emerald-900">
                <span className="font-medium tabular-nums">{matchPreview.toInsert}</span>{' '}
                new {matchPreview.toInsert === 1 ? 'item' : 'items'} will be added,{' '}
                <span className="font-medium tabular-nums">{matchPreview.toUpdate}</span>{' '}
                existing {matchPreview.toUpdate === 1 ? 'item' : 'items'} will be updated
                {matchPreview.unchanged > 0 && (
                  <>
                    , <span className="font-medium tabular-nums">{matchPreview.unchanged}</span>{' '}
                    already up to date
                  </>
                )}.
              </div>

              {matchPreview.hasVelocity && matchPreview.topSellers.length > 0 && (
                <div className="mt-3 border-t border-emerald-200/60 pt-2">
                  <div className="mb-1 text-[11px] font-medium text-emerald-900/60">
                    Top sellers in this file
                  </div>
                  <ul className="text-xs text-emerald-900/85">
                    {matchPreview.topSellers.map((r, i) => (
                      <li key={i} className="flex justify-between gap-2 py-0.5 tabular-nums">
                        <span className="truncate">{r.name}</span>
                        <span className="font-medium">
                          {r.quantity_sold} sold
                          {r.sale_price != null && ` @ ${formatMoney(r.sale_price)}`}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Cost estimation toggle (only meaningful if sale_price exists) */}
          {parsed?.rows?.length > 0 && parsed.columns?.priceIdx >= 0 && (
            <div className="rounded-xl border border-black/10 bg-white p-3">
              <label className="flex cursor-pointer items-start gap-3">
                <input
                  type="checkbox"
                  checked={estimateCost}
                  onChange={(e) => setEstimateCost(e.target.checked)}
                  className="mt-0.5 h-5 w-5 rounded border-black/30 text-blue-600 focus:ring-blue-600"
                />
                <div className="text-sm">
                  <div className="font-medium">Estimate cost as 30% of sale price</div>
                  <div className="mt-0.5 text-xs leading-snug text-black/55">
                    Industry-standard food cost is 28-32% of menu price. Turning
                    this on gives a starting cost estimate so waste tracking
                    shows ballpark dollars right away. You'll want to refine
                    these to actuals (per-pound costs from your invoices) later.
                    If unchecked, new items have $0 cost and waste tracking
                    won't show meaningful dollars until you set them.
                  </div>
                </div>
              </label>
            </div>
          )}

          {/* Per-row warnings */}
          {parsed?.errors?.length > 0 && parsed.rows.length > 0 && (
            <details className="rounded-xl border border-amber-200 bg-amber-50/40 p-3 text-xs text-amber-900">
              <summary className="cursor-pointer font-medium">
                {parsed.errors.length} row{parsed.errors.length === 1 ? '' : 's'} skipped — click to review
              </summary>
              <ul className="mt-2 space-y-1">
                {parsed.errors.slice(0, 20).map((msg, idx) => (
                  <li key={idx} className="leading-snug">{msg}</li>
                ))}
                {parsed.errors.length > 20 && (
                  <li className="text-amber-900/70">…and {parsed.errors.length - 20} more</li>
                )}
              </ul>
            </details>
          )}

          {!parsed && !error && (
            <p className="text-xs text-black/55">
              Existing items are matched by name (case-insensitive). The import
              fills in missing category and sale price on existing rows but
              never overwrites your edits. New items default to "each" unit
              and active.
            </p>
          )}
        </div>

        <div className="sticky bottom-0 z-10 border-t border-black/5 bg-white px-4 pt-3 safe-bottom">
          <button
            type="button"
            onClick={handleConfirm}
            disabled={submitting || !parsed?.rows?.length}
            className="min-h-btn w-full rounded-lg bg-blue-600 px-4 py-4 text-xl font-medium text-white shadow-sm hover:bg-blue-700 active:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting
              ? 'Importing\u2026'
              : parsed?.rows?.length
                ? `Import ${parsed.rows.length} ${parsed.rows.length === 1 ? 'item' : 'items'}`
                : 'Pick a CSV first'}
          </button>
        </div>
      </div>
    </div>
  )
}

function DetectedRow({ label, idx, headers, optional }) {
  const found = idx != null && idx >= 0
  return (
    <div className="flex justify-between gap-2">
      <span className="text-black/60">{label}{optional ? ' (optional)' : ''}</span>
      <span className={`truncate font-medium ${found ? 'text-emerald-700' : (optional ? 'text-black/40' : 'text-red-600')}`}>
        {found ? headers[idx] : (optional ? '\u2014' : 'not found')}
      </span>
    </div>
  )
}
